import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, KeyboardArrowDown, KeyboardArrowUp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { exportStyledExcel } from '../../../utils/excelExport';


const fmt     = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '-';
  const s = String(d).split('T')[0];
  const parts = s.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    // yyyy-mm-dd
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  // already formatted or unknown — return as-is
  return s;
};
const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SaleReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const history = useHistory();

  const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData('ReportApi', `/SaleReport/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch sale report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token && fromDate && toDate) {
      // Do not fetch if date range is invalid
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  const totalGrand   = data.reduce((s, r) => s + (parseFloat(r.GrandTotal)    || 0), 0);
  const totalBalance = data.reduce((s, r) => s + (parseFloat(r.BalanceAmount) || 0), 0);
  const totalAmount  = data.reduce((s, r) => s + (parseFloat(r.TotalAmount)   || 0), 0);
  const totalCash    = data.reduce((s, r) => s + (parseFloat(r.CashAmount)    || 0), 0);
  const totalCard    = data.reduce((s, r) => s + (parseFloat(r.CardAmount)    || 0), 0);
  const totalUPI     = data.reduce((s, r) => s + (parseFloat(r.UPIAmount)     || 0), 0);
  const totalQty     = data.reduce((s, r) => s + (parseFloat(r.TotalQuantity) || 0), 0);

  const handleExport = async () => {
    if (!data.length) return;
    const colHeaders = ['#','Bill No','Date','Customer','Contact','Type','Amount','Discount','Taxable','CGST','SGST','IGST','Freight','Frt.GST%','Frt.GST','Packing','Pkg.GST%','Pkg.GST','Round Off','Grand Total','Cash','Card','UPI','Balance'];
    const dataRows = data.map((r, i) => [
      i+1, r.BillNo, fmtDate(r.BillDate), r.CustomerName, r.ContactNumber, r.SaleType,
      //parseFloat(r.TotalQuantity)||0,
      parseFloat(r.TotalAmount)||0, parseFloat(r.DiscountAmount)||0,
      parseFloat(r.TaxableAmount)||0, parseFloat(r.CGSTAmount)||0, parseFloat(r.SGSTAmount)||0,
      parseFloat(r.IGSTAmount)||0, parseFloat(r.FreightAmount)||0, parseFloat(r.FreightGSTRate)||0,
      parseFloat(r.FreightGSTAmount)||0, parseFloat(r.PackingAmount)||0, parseFloat(r.PackingGSTRate)||0,
      parseFloat(r.PackingGSTAmount)||0, parseFloat(r.RoundOff)||0, parseFloat(r.GrandTotal)||0,
      parseFloat(r.CashAmount)||0, parseFloat(r.CardAmount)||0, parseFloat(r.UPIAmount)||0, parseFloat(r.BalanceAmount)||0,
    ]);
    const totalAmountExp   = data.reduce((s, r) => s + (parseFloat(r.TotalAmount) || 0), 0);
    const totalDiscount    = data.reduce((s, r) => s + (parseFloat(r.DiscountAmount) || 0), 0);
    const totalTaxable     = data.reduce((s, r) => s + (parseFloat(r.TaxableAmount) || 0), 0);
    const totalCGST        = data.reduce((s, r) => s + (parseFloat(r.CGSTAmount) || 0), 0);
    const totalSGST        = data.reduce((s, r) => s + (parseFloat(r.SGSTAmount) || 0), 0);
    const totalIGST        = data.reduce((s, r) => s + (parseFloat(r.IGSTAmount) || 0), 0);
    const totalFreight     = data.reduce((s, r) => s + (parseFloat(r.FreightAmount) || 0), 0);
    const totalFreightGST  = data.reduce((s, r) => s + (parseFloat(r.FreightGSTAmount) || 0), 0);
    const totalPacking     = data.reduce((s, r) => s + (parseFloat(r.PackingAmount) || 0), 0);
    const totalPackingGST  = data.reduce((s, r) => s + (parseFloat(r.PackingGSTAmount) || 0), 0);
    const totalRoundOff    = data.reduce((s, r) => s + (parseFloat(r.RoundOff) || 0), 0);
    const totalGrandExp    = data.reduce((s, r) => s + (parseFloat(r.GrandTotal) || 0), 0);
    const totalCashExp     = data.reduce((s, r) => s + (parseFloat(r.CashAmount) || 0), 0);
    const totalCardExp     = data.reduce((s, r) => s + (parseFloat(r.CardAmount) || 0), 0);
    const totalUPIExp      = data.reduce((s, r) => s + (parseFloat(r.UPIAmount) || 0), 0);
    const totalBalanceExp  = data.reduce((s, r) => s + (parseFloat(r.BalanceAmount) || 0), 0);

    const totalsRow = [
      '', '', '', '', 'TOTAL', '', totalAmountExp, totalDiscount, totalTaxable, totalCGST, totalSGST,
      totalIGST, totalFreight, '', totalFreightGST, totalPacking, '', totalPackingGST, totalRoundOff,
      totalGrandExp, totalCashExp, totalCardExp, totalUPIExp, totalBalanceExp,
    ];

      await exportStyledExcel({
          reportTitle: 'SALE REPORT',
          sheetName: 'Sale Report',
          fileName: `SaleReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: colHeaders,
          rows: dataRows,
          totalsRow,
          columnWidths: [4,12,12,20,12,8,7,10,10,10,9,9,9,9,8,9,9,8,9,8,12,10,10,10,12],
      });
  };

  const th  = { padding: '8px 10px', textAlign: 'left',  fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)', background: '#43a047', color: 'white' };
  const thR = { ...th, textAlign: 'right' };
  const td  = { padding: '7px 10px', textAlign: 'left',  fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };

  const toggleRow = (id) => setExpandedRow(prev => prev === id ? null : id);

  // Detail panel for expanded row
  const DetailPanel = ({ r }) => {
    const item = (label, value, color) => (
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 90 }}>
        <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: color || '#374151' }}>{value}</span>
      </div>
    );
    return (
      <tr>
        <td colSpan={8} style={{ padding: 0, background: '#f0f4ff', borderBottom: '2px solid #1b5e20' }}>
          <div style={{ padding: '10px 16px', display: 'flex', flexWrap: 'nowrap', gap: '16px', overflowX: 'auto' }}>
            {/* Amount breakdown */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', borderRight: '1px solid #d1d5db', paddingRight: 16 }}>
              {item('Amount', fmt(r.TotalAmount))}
              {item('Discount', fmt(r.DiscountAmount))}
              {item('Taxable', fmt(r.TaxableAmount))}
            </div>
            {/* Tax */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', borderRight: '1px solid #d1d5db', paddingRight: 16 }}>
              {item('CGST', fmt(r.CGSTAmount), '#1d4ed8')}
              {item('SGST', fmt(r.SGSTAmount), '#1d4ed8')}
              {item('IGST', fmt(r.IGSTAmount), '#1d4ed8')}
            </div>
            {/* Freight */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', borderRight: '1px solid #d1d5db', paddingRight: 16 }}>
              {item('Freight', fmt(r.FreightAmount))}
              {item('Frt.GST%', `${parseFloat(r.FreightGSTRate)||0}%`)}
              {item('Frt.GST', fmt(r.FreightGSTAmount))}
            </div>
            {/* Packing */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', borderRight: '1px solid #d1d5db', paddingRight: 16 }}>
              {item('Packing', fmt(r.PackingAmount))}
              {item('Pkg.GST%', `${parseFloat(r.PackingGSTRate)||0}%`)}
              {item('Pkg.GST', fmt(r.PackingGSTAmount))}
              {item('Round Off', parseFloat(r.RoundOff)||0)}
            </div>
            {/* Payment */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {item('Cash', fmt(r.CashAmount), '#059669')}
              {item('Card', fmt(r.CardAmount), '#1d4ed8')}
              {item('UPI', fmt(r.UPIAmount), '#7c3aed')}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading sale report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Sales Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  <th style={th}></th>
                  <th style={th}>#</th>
                  <th style={th}>Bill No</th>
                  <th style={th}>Date</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Type</th>
                  <th style={thR}>Grand Total</th>
                  <th style={{ ...thR, color: '#fca5a5' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.map((r, i) => {
                  const isOpen = expandedRow === (r.Id || i);
                  return (
                    <>
                      <tr
                        key={r.Id || i}
                        onClick={() => toggleRow(r.Id || i)}
                        style={{ background: isOpen ? '#e8edf8' : (i % 2 === 0 ? '#fff' : '#e8f5e9'), borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                      >
                        <td style={{ ...td, width: 28, color: '#1b5e20' }}>
                          {isOpen ? <KeyboardArrowUp sx={{ fontSize: 16 }} /> : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
                        </td>
                        <td style={td}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 600, color: '#1b5e20' }}>{r.BillNo || '-'}</td>
                        <td style={td}>{fmtDate(r.BillDate)}</td>
                        <td style={{ ...td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.CustomerName || '-'}</td>
                        <td style={td}>
                          <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: r.SaleType === 'Credit' ? '#fef3c7' : '#dbeafe', color: r.SaleType === 'Credit' ? '#d97706' : '#1d4ed8' }}>
                            {r.SaleType || '-'}
                          </span>
                        </td>
                        <td style={{ ...tdR, fontWeight: 700 }}>{fmt(r.GrandTotal)}</td>
                        <td style={{ ...tdR, fontWeight: 600, color: (parseFloat(r.BalanceAmount)||0) > 0 ? '#dc2626' : '#374151' }}>{fmt(r.BalanceAmount)}</td>
                      </tr>
                      {isOpen && <DetailPanel key={`d-${r.Id||i}`} r={r} />}
                    </>
                  );
                })}  
                {data.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: 13 }}>
                      {fetched ? 'No sale records found for the selected date range' : 'Select date range to load report'}
                    </td>
                  </tr>
                )}
              </tbody>
              {data.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#e8f5e9', color: '#1b5e20', fontWeight: 700, position: 'sticky', bottom: 0 }}>
                    <td colSpan={6} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                    <td style={tdR}>{fmt(totalGrand)}</td>
                    <td style={tdR}>{fmt(totalBalance)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
        </div>
      </div>
    </div>
  );
};

export default SaleReport;


